import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

const POLL_MS = 60000; // check every 60s
const VERSION_KEY = "force_build_id"; // last build id this device successfully loaded

/**
 * Ensures the client always runs the latest deployed build.
 *
 * On EVERY load (web or installed PWA) it asks the backend for the current
 * build id and compares it to the id this device last loaded (persisted in
 * localStorage). If the server is newer, it wipes the service-worker + Cache
 * Storage and hard-reloads so fresh HTML, hashed JS/CSS and images are fetched.
 * It also keeps polling while open so a mid-session deploy updates too.
 */
export default function VersionWatcher() {
    const updating = useRef(false);

    useEffect(() => {
        let timer;

        // If we just auto-updated on the previous load, confirm it so it feels intentional.
        // Deferred so the <Toaster> is mounted/subscribed before we fire (sonner drops
        // toasts queued during the same first commit).
        try {
            if (sessionStorage.getItem("force_just_updated")) {
                sessionStorage.removeItem("force_just_updated");
                setTimeout(() => toast.success("Updated to the latest version", { duration: 3000 }), 600);
            }
        } catch (e) { /* ignore */ }

        async function clearAndReload(nextVersion) {
            try {
                // Record the version we're updating to BEFORE reload so we can't loop.
                if (nextVersion) localStorage.setItem(VERSION_KEY, nextVersion);
                sessionStorage.setItem("force_just_updated", "1");
                if ("serviceWorker" in navigator) {
                    const regs = await navigator.serviceWorker.getRegistrations();
                    await Promise.all(regs.map((r) => r.unregister()));
                }
                if (window.caches) {
                    const keys = await caches.keys();
                    await Promise.all(keys.map((k) => caches.delete(k)));
                }
            } catch (e) {
                /* best effort */
            }
            // cache-busted reload -> nginx serves fresh index.html + new hashed assets
            const url = new URL(window.location.href);
            url.searchParams.set("v", Date.now().toString());
            window.location.replace(url.toString());
        }

        async function check() {
            try {
                const { data } = await api.get("/version", {
                    params: { t: Date.now() },
                    headers: { "Cache-Control": "no-cache" },
                });
                const v = data?.version;
                if (!v || updating.current) return;
                const known = localStorage.getItem(VERSION_KEY);
                if (!known) {
                    // First time on this device — adopt the current build as our baseline.
                    localStorage.setItem(VERSION_KEY, v);
                    return;
                }
                if (v !== known) {
                    // Server is running a different (newer) build than the one this
                    // device cached — purge and reload to pick it up.
                    updating.current = true;
                    toast.info("Updating to the latest version…", { duration: 2000 });
                    setTimeout(() => clearAndReload(v), 800);
                }
            } catch (e) {
                /* offline / backend restarting — ignore */
            }
        }

        // Register the service worker (installable PWA) and keep it fresh.
        let swReg = null;
        let onControllerChange = null;
        if ("serviceWorker" in navigator) {
            // If a NEW service worker takes control (i.e. a new build was deployed),
            // hard-reload exactly once so fresh HTML/JS/images load automatically —
            // no manual refresh. Guarded so it can't loop and won't fire on the very
            // first install (when there was no previous controller).
            const hadController = !!navigator.serviceWorker.controller;
            onControllerChange = () => {
                if (!hadController || updating.current) return;
                if (sessionStorage.getItem("force_sw_reloaded")) return;
                updating.current = true;
                sessionStorage.setItem("force_sw_reloaded", "1");
                sessionStorage.setItem("force_just_updated", "1");
                window.location.reload();
            };
            navigator.serviceWorker.addEventListener("controllerchange", onControllerChange);
            navigator.serviceWorker
                .register("/sw.js")
                .then((reg) => {
                    swReg = reg;
                    reg.update();
                })
                .catch(() => {});
        }
        function refreshSW() {
            if (swReg) swReg.update();
        }

        check();
        timer = setInterval(check, POLL_MS);
        const onVisible = () => {
            if (document.visibilityState === "visible") {
                check();
                refreshSW();
            }
        };
        document.addEventListener("visibilitychange", onVisible);
        window.addEventListener("focus", check);

        return () => {
            clearInterval(timer);
            document.removeEventListener("visibilitychange", onVisible);
            window.removeEventListener("focus", check);
            if (onControllerChange && "serviceWorker" in navigator) {
                navigator.serviceWorker.removeEventListener("controllerchange", onControllerChange);
            }
        };
    }, []);

    return null;
}
